import { isEmpty } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import type { TokenAutomation } from "hooks/firestore/v2/models/tokenAutomation";
import { Pill } from "components/Pill";
import { useBetslip, useDispatch } from "hooks";
import {
  setMultiTokenAllocationId,
  setTokenAllocationId,
} from "sections/Betting/Betslip/betslipSlice";
import { RadioGroup, RadioGroupItem } from "components/Radio";

import { useTokenAllocations } from "../hooks/useTokenAllocations";

import { ReactComponent as WinningsBoostIcon } from "sections/Betting/Betslip/assets/winnings.svg";
import { ReactComponent as ReturnIcon } from "sections/Betting/Betslip/assets/return.svg";
import type { Always, Nullable, NullableMaybe } from "types/utilities";

import * as styles from "./Tokens.module.scss";
import type { Selection } from "../hooks/useSelection";
import type { Context } from "utilities/promotions/models/context";

type TokenProps = {
  outcomeIds: string[];
  selectedAllocationId: Nullable<string>;
  selection: Selection;
};

type ActionType = Always<TokenAutomation["actions"][number]["type"]>;

const beautifyNumbersRegex = /(\$[0-9,]+(\.[0-9]{2})?)|([0-9]+(\.[0-9]+)?%)/g;

const beautifyNumbers = (text: string, className: string) => {
  return text.replace(beautifyNumbersRegex, (match) => {
    return `<span class="${styles.highlight} ${className}">${match}</span>`;
  });
};

const typeToNameMap: Record<string, string> = {
  STAKE_REFUND: "Bet Reload",
  WINNINGS_BOOST: "Winnings Boost",
};

const byBetslipPriority = (a: TokenAutomation, b: TokenAutomation) =>
  (b?.attributes?.tokenBetSlipPriority ?? 0) -
  (a?.attributes?.tokenBetSlipPriority ?? 0);

function getLegBasedBetslipInfo(
  isSEM: boolean,
  selection: Selection,
  selections: string[],
  legBasedBetSlipInfo: NullableMaybe<Record<string, string>>,
) {
  const legBasedInfo = Object.values(legBasedBetSlipInfo ?? {});

  // info displayed here depends on the number of legs, so we count
  // the number of subOutcomes (for sem) or selections and that value
  // will be the position in the array we need to pull the copy from
  const legCount = isSEM
    ? selection.subOutcomes?.length ?? 0
    : selections.length;

  // if the number of legs is larger than the copy list we just use
  // the last one
  const infoIndexToGrab =
    legCount > legBasedInfo.length ? legBasedInfo.length - 1 : legCount - 1;

  return legBasedInfo[infoIndexToGrab] ?? "";
}

const Tokens = ({
  selection,
  outcomeIds,
  selectedAllocationId,
}: TokenProps) => {
  const dispatch = useDispatch();
  const [alreadyAutoSelected, setAlreadyAutoSelected] = useState(false);

  const { isLoading, allocations, automations, usedAllocations } =
    useTokenAllocations(outcomeIds);
  const sortedAutomations = (automations ?? []).sort(byBetslipPriority);

  const automationPriorityMap = new Map(
    sortedAutomations.map((automation) => [
      automation.automationId,
      automation.attributes?.tokenBetSlipPriority ?? 0,
    ]),
  );

  const sortedAllocations = (allocations ?? []).sort((a, b) => {
    // automations are sorted by betslip priority field, allocations however have
    // no useful attributes on them to respect this sorting so this map helps ensure
    // that we respect the automation order when selection allocations
    const aPriority = automationPriorityMap.get(a.automationId) ?? 0;
    const bPriority = automationPriorityMap.get(b.automationId) ?? 0;

    return bPriority - aPriority;
  });

  const {
    props: {
      betType,
      betslipIsReviewingMessages,
      betslipIsSubmitting,
      selections,
    },
  } = useBetslip();

  const isReviewingOrSubmitting =
    betslipIsReviewingMessages || betslipIsSubmitting;

  const shouldHide = isReviewingOrSubmitting && !selectedAllocationId;

  const isMulti = betType === "MULTI";
  const isSEM = !isEmpty(selection.subOutcomes);

  // build a list of available automation types for allocations that we have available
  const automationTypes = useMemo(() => {
    if (isLoading || !automations || !allocations) return [];

    return (allocations || []).reduce<ActionType[]>((types, allocation) => {
      const automation = sortedAutomations.find(
        (automation) => automation.automationId === allocation.automationId,
      );

      const action = automation?.actions?.[0];

      if (!action || types.includes(action?.type)) return types;

      types.push(action.type);

      return types;
    }, []);
  }, [allocations, automations, isLoading]);

  const selectedAutomationType = useMemo(() => {
    const allocation = sortedAllocations.find(
      (allocation) => allocation.id === selectedAllocationId,
    );

    const automation = sortedAutomations.find(
      (automation) => automation.automationId === allocation?.automationId,
    );

    if (!automation) return;

    const action = automation?.actions?.[0];
    return action?.type;
  }, [selectedAllocationId, automations, allocations]);

  const automationsForSelectedType = useMemo(() => {
    if (!selectedAutomationType || !sortedAutomations) return [];

    return sortedAutomations.filter((automation) => {
      const action = automation?.actions?.[0];
      const hasAllocations = sortedAllocations.some(
        (allocation) => allocation.automationId === automation.automationId,
      );
      return action?.type === selectedAutomationType && hasAllocations;
    });
  }, [selectedAutomationType, sortedAutomations]);

  const setAllocationId = (allocationId: string | null) => {
    if (isMulti) {
      dispatch(setMultiTokenAllocationId(allocationId));
      return;
    }

    const outcomeId = outcomeIds[0];
    dispatch(
      setTokenAllocationId({
        outcomeId,
        allocationId,
      }),
    );
  };

  const toggleAllocationId = (tokenId: string) => {
    if (selectedAllocationId === tokenId) {
      setAllocationId(null);
      return;
    }

    setAllocationId(tokenId);
  };

  const excludeNonEventBasedAutomationTokens = (
    allocation: (typeof sortedAllocations)[number],
  ) =>
    sortedAutomations.find((automation) => {
      return (
        (allocation?.automationId === automation.automationId ||
          automation.actions?.[0]?.type === selectedAutomationType) &&
        automation?.attributes?.eventBasedTokens
      );
    });

  useEffect(() => {
    if (isLoading || alreadyAutoSelected || selectedAllocationId) return;

    const firstAvailableFreeAllocation = sortedAllocations
      .filter(excludeNonEventBasedAutomationTokens)
      .find((allocation) => !allocation.usedAtOutcomeId);

    if (!firstAvailableFreeAllocation) return;

    setAllocationId(firstAvailableFreeAllocation.id);
    setAlreadyAutoSelected(true);
  }, [isLoading, allocations, selectedAllocationId]);

  if (isEmpty(sortedAllocations) || shouldHide) {
    return null;
  }

  return (
    <>
      <div className={styles.automations}>
        {automationTypes.map((type) => {
          const firstAllocation =
            sortedAllocations.find((allocation) => {
              const automation = sortedAutomations.find(
                (automation) =>
                  automation.automationId === allocation?.automationId,
              );
              const action = automation?.actions?.[0];

              return (
                allocation &&
                action?.type === type &&
                typeof usedAllocations[allocation.id] === "undefined"
              );
            }) ??
            sortedAllocations.find((allocation) => {
              const automation = sortedAutomations.find(
                (automation) =>
                  automation.automationId === allocation?.automationId,
              );
              const action = automation?.actions?.[0];

              return action?.type === type;
            });

          if (!firstAllocation) return;

          return (
            <Pill
              className={styles.automation}
              key={`type-${type}`}
              disabled={isReviewingOrSubmitting}
              onPressedChange={(isPressed) => {
                if (
                  !isPressed &&
                  selectedAllocationId &&
                  selectedAllocationId === selection.allocationId
                ) {
                  // if token type is switching from ON to OFF
                  // then we want to just de-select the selected allocation it
                  toggleAllocationId(selectedAllocationId);
                  return;
                }

                if (!firstAllocation) return;
                if (firstAllocation.usedAtOutcomeId) {
                  // if this allocation already used somewhere then we need to de-select it before toggling
                  if (isMulti) {
                    dispatch(setMultiTokenAllocationId(null));
                  } else {
                    dispatch(
                      setTokenAllocationId({
                        outcomeId: firstAllocation.usedAtOutcomeId,
                        allocationId: null,
                      }),
                    );
                  }
                }

                toggleAllocationId(firstAllocation.id);
              }}
              pressed={type === selectedAutomationType}
            >
              <span className={styles.icon}>
                {type === "WINNINGS_BOOST" ? (
                  <WinningsBoostIcon />
                ) : (
                  <ReturnIcon />
                )}
              </span>
              {typeToNameMap[type]}
            </Pill>
          );
        })}
      </div>

      {automationsForSelectedType.length > 0 && (
        <RadioGroup className={styles.automationSelector}>
          {automationsForSelectedType
            .sort(byBetslipPriority)
            .map((automation) => {
              const allocationsForSelectedAutomationType =
                sortedAllocations.filter(
                  (allocation) =>
                    allocation.automationId === automation.automationId,
                );

              const isChecked = allocationsForSelectedAutomationType.some(
                (allocation) => allocation.id === selectedAllocationId,
              );

              const legBasedBetslipCopy = getLegBasedBetslipInfo(
                isSEM,
                selection,
                selections,
                automation?.legBasedBetSlipInfo,
              );

              return (
                <RadioGroupItem
                  className={styles.item}
                  key={`allocation-${automation.automationId}`}
                  id={`allocation-${automation.automationId}`}
                  label={
                    <>
                      {automation.attributes?.tokenInfoPrefix && (
                        <span className={styles.prefix}>
                          {automation.attributes.tokenInfoPrefix}:{" "}
                        </span>
                      )}

                      {automation.actions?.[0]?.info && (
                        <span
                          className={styles.label}
                          dangerouslySetInnerHTML={{
                            __html: beautifyNumbers(
                              automation.actions[0].info,
                              selectedAutomationType === "WINNINGS_BOOST"
                                ? styles.orange
                                : styles.green,
                            ),
                          }}
                        />
                      )}

                      {legBasedBetslipCopy && (
                        <span className={styles.label}>
                          {" "}
                          {legBasedBetslipCopy}
                        </span>
                      )}
                    </>
                  }
                  value={automation.automationId}
                  checked={isChecked}
                  onClick={() => {
                    const firstAllocation =
                      allocationsForSelectedAutomationType.find(
                        (allocation) => !allocation.usedAtOutcomeId,
                      ) ?? allocationsForSelectedAutomationType[0];

                    if (!firstAllocation) return;
                    if (firstAllocation.usedAtOutcomeId) {
                      // if this allocation already used somewhere then we need to de-select it before toggling
                      if (isMulti) {
                        dispatch(setMultiTokenAllocationId(null));
                      } else {
                        dispatch(
                          setTokenAllocationId({
                            outcomeId: firstAllocation?.usedAtOutcomeId,
                            allocationId: null,
                          }),
                        );
                      }
                    }

                    toggleAllocationId(firstAllocation.id);
                  }}
                />
              );
            })}
        </RadioGroup>
      )}
    </>
  );
};

export { Tokens };
